<template>
  <div v-if="article" class="article-page">
    <div class="wrap">
      <div class="article-grid">
        <main>
          <!-- Structured header (eyebrow / headline / standfirst / byline / from-mag),
               mirroring assets/articles/article-page.html. The equivalent header text inside
               the article HTML is hidden via ::v-deep to avoid duplication. -->
          <header class="article-head">
            <span v-if="articleEyebrow" class="eyebrow">{{ articleEyebrow }}</span>
            <h1 class="headline">{{ article.headline }}</h1>
            <p v-if="articleStandfirst" class="standfirst">{{ articleStandfirst }}</p>
            <div v-if="articleByline" class="byline-row">
              <span class="byline">{{ articleByline }}</span>
            </div>
            <div class="from-mag">
              <span v-if="coverImage" class="mini-cover"><img :src="coverImage" :alt="`${article.title} cover`"/></span>
              <span
                >This feature appears in
                <a :href="article.urlPath"
                  ><b>{{ article.title }} magazine</b></a
                >
                &mdash; available in print, delivered to your door.</span
              >
            </div>
          </header>

          <!-- Article body sourced from assets/articles/<FourLetterCode>.html via lib/data/articles.js.
               Styled locally (scoped, via ::v-deep) to match assets/articles/article-page.html. -->
          <article class="jmag-article">
            <!-- eslint-disable-next-line vue/no-v-html -->
            <div class="jmag-article__body" v-html="article.html"></div>
          </article>

          <!-- End-of-article conversion panel: filled from the default PRINT offer -->
          <aside v-if="hasOffer" class="end-cta">
            <div class="cta-head">Read the full feature in print</div>
            <div class="cta-body">
              <span v-if="coverImage" class="cover"><img :src="coverImage" :alt="`${offerTitle} cover`"/></span>
              <div class="offer">
                <h3>{{ offerTitle }}</h3>
                <p class="price">
                  <b>{{ formatCurrency(defaultPrintOffer.price) }}</b> for your first {{ defaultPrintOffer.numOfIssues }} issues
                  <template v-if="showSaving">
                    &nbsp;<s>{{ formatCurrency(defaultPrintOffer.rrp) }}</s
                    >&nbsp;
                    <b class="save-inline">Save {{ defaultPrintOffer.save }}%</b>
                  </template>
                </p>
                <ul>
                  <li>Free UK delivery, direct to your door</li>
                  <li>Instant digital access while you wait for your first issue</li>
                  <li>Never miss an issue &mdash; cancel anytime</li>
                </ul>
              </div>
              <div class="buy-col">
                <a class="btn block" :class="{ 'is-loading': addingToBasket }" href="#" title="add to basket" :aria-busy="addingToBasket" @click.prevent="handleAddToBasket">
                  <span class="btn-label">Buy now</span>
                  <span v-if="addingToBasket" class="btn-spinner" aria-hidden="true"></span>
                </a>
                <a :href="article.urlPath" class="see-all">See all offers &amp; gift options</a>
                <small>{{ magazine.h1Title || magazine.title }}</small>
              </div>
            </div>
          </aside>
        </main>

        <!-- Right rail -->
        <aside class="rail">
          <div class="rail-sticky">
            <div v-if="hasOffer" class="rail-box rail-offer">
              <div class="rail-head">As featured in</div>
              <div class="rail-inner">
                <span v-if="coverImage" class="cover"><img :src="coverImage" :alt="`${offerTitle} cover`"/></span>
                <h3>{{ magazine.h1Title || magazine.title }}</h3>
                <p class="price">
                  {{ defaultPrintOffer.numOfIssues }} issues for <b>{{ formatCurrency(defaultPrintOffer.price) }}</b>
                </p>
                <p v-if="showSaving" class="save">Saving {{ defaultPrintOffer.save }}%</p>
                <a class="btn block" :class="{ 'is-loading': addingToBasket }" href="#" title="add to basket" :aria-busy="addingToBasket" @click.prevent="handleAddToBasket">
                  <span class="btn-label">Add to basket</span>
                  <span v-if="addingToBasket" class="btn-spinner" aria-hidden="true"></span>
                </a>
                <small>Free UK delivery &middot; Cancel anytime</small>
              </div>
            </div>

            <div class="rail-box newsletter">
              <div class="rail-head grey">Free reads, weekly</div>
              <div class="rail-inner">
                <p>Our best free articles and subscriber-only offers, in your inbox every Friday.</p>
                <a class="btn block" href="/newsletter-signup" target="_blank" rel="noopener">Sign me up</a>
                <small>We'll only use your email to send the newsletter. Unsubscribe anytime. See our privacy policy.</small>
              </div>
            </div>

            <div class="rail-box more-reads">
              <div class="rail-head grey">More free reads</div>
              <div class="rail-inner">
                <ul>
                  <li v-for="read in moreReads" :key="read.slug">
                    <span class="cat">{{ read.title }}</span
                    ><a :href="read.routePath">{{ read.headline }}</a>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </aside>
      </div>
    </div>

    <!-- Related articles -->
    <div class="related">
      <div class="wrap">
        <h2>More from Free Reads</h2>
        <div class="card-row">
          <div v-for="related in relatedArticles" :key="related.slug" class="card">
            <a :href="related.routePath" class="card-media">
              <img v-if="related.image" :src="related.image" :alt="related.headline" loading="lazy" />
              <span v-else class="ph">Image &middot; 16:10</span>
            </a>
            <div class="card-txt">
              <span class="cat">{{ related.title }}</span>
              <h3>
                <a :href="related.routePath">{{ related.headline }}</a>
              </h3>
              <span class="meta">{{ related.issue }}</span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <footer class="article-footer">
      <div class="wrap">
        <span>&copy; {{ currentYear }} magazine.co.uk &mdash; The UK's Leading Magazine Subscription Site</span>
        <span class="footer-links">
          <a href="/delivery-policy" title="Delivery" target="_blank" rel="noopener">Delivery</a>
          &nbsp;&middot;&nbsp;
          <a href="/frequently-asked-questions" title="FAQs" target="_blank" rel="noopener">FAQs</a>
          &nbsp;&middot;&nbsp;
          <a href="/terms-and-conditions" title="Terms &amp; Conditions" target="_blank" rel="noopener">Terms &amp; Conditions</a>
          &nbsp;&middot;&nbsp;
          <a href="/privacy-notice" title="Privacy Policy" target="_blank" rel="noopener">Privacy</a>
          &nbsp;&middot;&nbsp;
          <a href="/about-us" title="About us" target="_blank" rel="noopener">About us</a>
          &nbsp;&middot;&nbsp;
          <a href="/contact" title="Contact us" target="_blank" rel="noopener">Contact us</a>
        </span>
      </div>
    </footer>
  </div>
</template>

<script>
import { articles, articlesBySlug } from '~/lib/data/articles'
import { callAWSFrontendAPI } from '~/lib/util/aws'
import { formatCurrency } from '~/lib/util/basket'
import basketMixin from '~/mixins/basket.vue'
import gtmProductPageMixin from '@/mixins/gtm/product-page'

// Grab the first <img> src from an article's HTML, used as the card placeholder image.
function firstImageFromHtml(html) {
  if (!html) {
    return ''
  }
  const match = html.match(/<img[^>]+src=["']([^"']+)["']/i)
  return match ? match[1] : ''
}

// Strip HTML tags/entities from a fragment and collapse whitespace to a single line.
function stripHtmlTags(html) {
  return (html || '')
    .replace(/<[^>]*>/g, ' ')
    .replace(/&nbsp;/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
}

// Extract the text of the first `<tag class="...className...">...</tag>` in an article's HTML.
function extractText(html, tag, className) {
  if (!html) {
    return ''
  }
  const re = new RegExp('<' + tag + '[^>]*class="[^"]*' + className + '[^"]*"[^>]*>([\\s\\S]*?)</' + tag + '>', 'i')
  const match = html.match(re)
  return match ? stripHtmlTags(match[1]) : ''
}

// Fisher–Yates shuffle, then take the first `count` items.
function pickRandom(pool, count) {
  const arr = [...pool]
  for (let i = arr.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1))
    ;[arr[i], arr[j]] = [arr[j], arr[i]]
  }
  return arr.slice(0, count)
}

// Reduce a full article record to just what the "more reads" / "related" cards need.
function toCard(a) {
  return {
    slug: a.slug,
    headline: a.headline,
    title: a.title,
    issue: a.issue,
    routePath: a.routePath,
    image: firstImageFromHtml(a.html)
  }
}

export default {
  name: 'Article',
  layout: 'articles',

  mixins: [gtmProductPageMixin, basketMixin],

  async fetch() {
    // Call the AWS Frontend API using the article's URLPath (from the CSV),
    // mirroring mixins/product.vue, and store the response in dataUI.
    if (!this.article || !this.article.urlPath) {
      return
    }

    const path = this.article.urlPath
    const query = this.$nuxt.context.query
    const useCache = this.$store.getters['UI/useCache']

    console.log('page=ARTICLE')
    console.log('path=', path)
    console.log('QS useCache=', useCache)
    console.log('AWSAPIGatewayURL=', this.$nuxt.$config.AWSAPIGatewayURL)

    this.dataUI = {
      ...(await callAWSFrontendAPI({ context: this, path, query, useCache, type: 'PRODUCTPAGE' }))
    }

    console.log('ARTICLE dataUI.magazine=', this.dataUI && this.dataUI.magazine)
    console.log('ARTICLE offers.PRINT=', this.dataUI && this.dataUI.offers && this.dataUI.offers.PRINT)
    console.log('ARTICLE defaultPrintOffer=', this.defaultPrintOffer)

    // The article itself is always valid regardless of the product API result,
    // so keep the page rendering even if the API returned a non-200 status.
    this.$store.commit('UI/statusCode', 200, { root: true })
  },

  asyncData({ route, store }) {
    // Article routes are registered as `/{URLPath}/{Issue}/articles/{articleSlug}`,
    // so the article slug is always the last path segment.
    const slug =
      route.path
        .split('/')
        .filter(Boolean)
        .pop() || ''
    const article = articlesBySlug[slug] || null

    if (!article) {
      store.commit('UI/statusCode', 404, { root: true })
      return { article: null, moreReads: [], relatedArticles: [] }
    }

    store.commit('UI/statusCode', 200, { root: true })

    // Pool of other articles to surface as "more reads" (rail) and "related" cards.
    // Randomised on every render (server first load, then client-side on navigation).
    const pool = articles.filter(a => a.slug !== article.slug)
    const moreReads = pickRandom(pool, 4).map(toCard)
    const relatedArticles = pickRandom(pool, 3).map(toCard)

    // Breadcrumbs: HOME > articles > {slug}
    store.commit(
      'UI/breadcrumbs',
      [
        { first: true, value: 'magazine subscriptions', url: '/', last: false },
        { first: false, value: article.title, url: article.urlPath, last: false },
        { first: false, value: article.headline, url: article.routePath, last: true }
      ],
      { root: true }
    )

    return { article, moreReads, relatedArticles }
  },

  data() {
    return {
      dataUI: {},
      moreReads: [],
      relatedArticles: [],
      addingToBasket: false
    }
  },

  fetchOnServer: true,

  computed: {
    currentYear() {
      return new Date().getFullYear()
    },
    magazine() {
      return (this.dataUI && this.dataUI.magazine) || null
    },
    defaultPrintOffer() {
      const offers = this.dataUI && this.dataUI.offers && this.dataUI.offers.PRINT
      if (!offers || !offers.length) {
        return null
      }
      // Cheapest / default offer flagged by the API.
      return offers.find(o => o.isDefault) || offers[0]
    },
    hasOffer() {
      return !!(this.magazine && this.defaultPrintOffer)
    },
    coverImage() {
      if (!this.magazine || !this.magazine.imageUrl) {
        return ''
      }
      const url = `${this.$config.imgixURL}${this.magazine.imageUrl}`
      return `${url}${url.includes('?') ? '&' : '?'}dpr=2`
    },
    // Eyebrow / category badge: hero eyebrow, else the masthead section (ignoring 'unlisted'),
    // else the article standfirst kicker.
    articleEyebrow() {
      const html = this.article && this.article.html
      const hero = extractText(html, 'p', 'hero-eyebrow')
      if (hero) {
        return hero
      }
      const category = extractText(html, 'div', 'masthead-section')
      if (category && category.toLowerCase() !== 'unlisted') {
        return category
      }
      return extractText(html, 'p', 'article-standfirst')
    },
    // Standfirst / lead paragraph.
    articleStandfirst() {
      const html = this.article && this.article.html
      return extractText(html, 'p', 'article-full-lead') || extractText(html, 'p', 'hero-lead') || extractText(html, 'p', 'article-standfirst')
    },
    // Author byline.
    articleByline() {
      const html = this.article && this.article.html
      return extractText(html, 'p', 'article-full-byline') || extractText(html, 'p', 'hero-byline')
    },
    offerTitle() {
      if (!this.magazine) {
        return ''
      }
      return `${this.magazine.h1Title || this.magazine.title} Subscription`
    },
    showSaving() {
      const offer = this.defaultPrintOffer
      return !!(offer && offer.save && Number(offer.rrp) > Number(offer.price))
    }
  },

  methods: {
    formatCurrency(value) {
      return formatCurrency(value)
    },

    async handleAddToBasket() {
      if (this.addingToBasket) {
        return
      }

      const magazine = this.magazine
      const offer = this.defaultPrintOffer

      console.log('ArticlePage handleAddToBasket magazine=', magazine)
      console.log('ArticlePage handleAddToBasket offer=', offer)

      if (!magazine || !offer) {
        return
      }

      this.addingToBasket = true

      try {
        // Mirrors DesktopOffer.vue: build the basket item then update the BE basket.
        const item = {
          title: magazine.title,
          imageUrl: magazine.imageUrl,
          productId: magazine.productId,
          id: magazine.id,
          sku: magazine.sku,
          offer,
          category: magazine.category,
          subCategory: magazine.subCategory
        }

        // Update the BE Basket data
        const ret = await this.updateBasket({ item, action: 'add' })

        // GTM & FB push events integration
        !ret.error && this.pushGTMAndFBAfterButtonClick({ action: 'add', item })

        // Show the basket overlay
        !ret.error && this.$nuxt.$emit('SHOW_BASKET', true)
      } finally {
        this.addingToBasket = false
      }
    }
  },

  head() {
    if (!this.article) {
      return { title: 'Article not found' }
    }
    const title = `${this.article.headline} | ${this.article.title}`
    return {
      title,
      link: [{ rel: 'canonical', href: `${this.$config.baseURL}${this.article.routePath}` }],
      meta: [
        { hid: 'og:title', name: 'og:title', property: 'og:title', content: title },
        { hid: 'og:type', name: 'og:type', property: 'og:type', content: 'article' },
        { hid: 'robots', name: 'robots', content: 'NOINDEX,NOFOLLOW' }
      ]
    }
  }
}
</script>

<!-- Scoped: page chrome + the injected article content (styled via ::v-deep). All styling
     is aligned to the mockup at assets/articles/article-page.html; there is no external
     stylesheet (the pocketmags articles.css link has been removed). -->
<style lang="scss" scoped>
h1 {
  text-transform: uppercase;
}

.article-page {
  --red: #e2231a;
  --red-dark: #c11b13;
  --green: #2e9e44;
  --green-dark: #268238;
  --ink: #333333;
  --ink-soft: #5a5a5a;
  --bar: #58595b;
  --panel: #f0f0f1;
  --line: #dcdcde;
  --white: #ffffff;
  --chrome: Arial, Helvetica, sans-serif;
  --serif: Georgia, 'Times New Roman', serif;

  font-family: var(--chrome);
  color: var(--ink);
  background: var(--white);

  a {
    color: var(--red);
    text-decoration: none;

    &:hover {
      text-decoration: underline;
    }
  }
}

.wrap {
  max-width: 1240px;
  margin: 0 auto;
  padding: 0 20px;
}

.article-grid {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 300px;
  gap: 48px;
  padding: 18px 0 40px;
}

// min-width:0 lets the columns shrink to the track; without it a wide image keeps
// the grid item at its intrinsic width and spills past the wrap padding.
.article-grid > * {
  min-width: 0;
}

// Neutralise the global `main { width:100vw; min-height:100vh }` rule (assets/css/global.scss,
// applied <=767px) that otherwise makes this column viewport-wide and pushes images past the padding.
.article-grid > main {
  width: auto;
  min-height: 0;
}

/* ============ ARTICLE HEADER (eyebrow / headline / standfirst / byline / from-mag) ============ */
.eyebrow {
  display: inline-block;
  background: var(--red);
  color: #fff;
  font-family: var(--chrome);
  font-size: 11px;
  font-weight: bold;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  padding: 5px 10px;
}

.headline {
  font-family: var(--serif);
  font-size: 40px;
  line-height: 1.15;
  font-weight: normal;
  color: var(--ink);
  margin: 16px 0 12px;
}

.standfirst {
  font-family: var(--serif);
  font-size: 20px;
  line-height: 1.5;
  color: var(--ink-soft);
  margin: 0 0 20px;
  max-width: 66ch;
}

.byline-row {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px 22px;
  border-top: 1px solid var(--line);
  border-bottom: 1px solid var(--line);
  padding: 12px 0;
  font-family: var(--chrome);
  font-size: 13px;
  color: var(--ink-soft);

  b {
    color: var(--ink);
  }
}

.from-mag {
  display: flex;
  align-items: center;
  gap: 10px;
  font-family: var(--chrome);
  font-size: 13px;
  color: var(--ink-soft);
  padding: 12px 0 4px;

  .mini-cover {
    width: 26px;
    flex: none;
  }
}

/* ============ ARTICLE CONTENT (injected via v-html) ============
   Deep selectors are required because v-html children don't receive the
   scoped-CSS attribute. Styling mirrors the .article-body block of the mockup. */
.jmag-article {
  padding: 8px 0 8px;
}

.jmag-article__body {
  font-family: var(--serif);
  font-size: 18px;
  line-height: 1.7;
  color: var(--ink);

  ::v-deep {
    // The header (eyebrow / headline / standfirst / byline) is rendered explicitly in the
    // Vue template above, so hide the equivalent pocketmags header inside the article body.
    .masthead,
    .article-full-headline,
    .hero-headline,
    .article-full-lead,
    .hero-lead,
    .article-standfirst,
    .article-full-byline,
    .hero-byline,
    .hero-eyebrow {
      display: none;
    }

    // image credit (kept — appears within the article body)
    .articleImageCredit {
      font-family: var(--chrome);
      font-size: 13px;
      color: var(--ink-soft);
      margin: 0 0 12px;
    }

    // section subheads
    .article-subhead {
      font-family: var(--serif);
      font-weight: normal;
      font-size: 27px;
      line-height: 1.25;
      color: var(--ink);
      margin: 40px 0 14px;
    }

    // sub-subheads sit a level below section subheads: smaller bold sans-serif labels
    .article-subsubhead {
      font-family: var(--chrome);
      font-weight: bold;
      font-size: 16px;
      line-height: 1.3;
      letter-spacing: 0.02em;
      color: var(--ink);
      margin: 26px 0 8px;
    }

    // body copy
    .article-full-body {
      margin: 0 0 24px;
    }

    // Constrain flowing prose to a comfortable reading measure (~66ch). Images and
    // figures stay full column width for impact; only the text column is narrowed.
    .article-full-body,
    .article-subhead,
    .article-subsubhead,
    .article-pull-quote,
    .article-pull-quote-credit,
    .review-q,
    .spec-list,
    .verdict {
      max-width: 40rem;
    }

    // ---- Recipe layout ----------------------------------------------------
    // Separate consecutive recipes within one article
    .recipe {
      padding-top: 4px;
    }

    .recipe + .recipe {
      border-top: 1px solid var(--line);
      margin-top: 44px;
      padding-top: 40px;
    }

    // Ingredients -> tinted panel, two columns on wider screens.
    // Source OCR wraps every ingredient in .ld_bold; we normalise to regular weight.
    .article-full-ingredients-list {
      font-family: var(--chrome);
      font-size: 15px;
      line-height: 1.45;
      color: var(--ink);
      background: var(--panel);
      border-top: 3px solid var(--red);
      padding: 18px 22px 20px;
      margin: 26px 0;

      @media (min-width: 600px) {
        columns: 2;
        column-gap: 34px;
      }
    }

    .article-full-ingredients {
      position: relative;
      margin: 0 0 9px;
      padding-left: 15px;
      break-inside: avoid;

      &::before {
        content: '';
        position: absolute;
        left: 0;
        top: 0.55em;
        width: 5px;
        height: 5px;
        border-radius: 50%;
        background: var(--red);
      }

      .ld_bold {
        font-weight: normal;
      }
    }

    // Method steps -> tighter, with a coloured step number
    .article-full-step-list {
      margin: 0;
    }

    .article-full-step {
      margin: 0 0 16px;
      line-height: 1.6;

      .ld_bold:first-child {
        font-family: var(--chrome);
        font-weight: bold;
        color: var(--red);
      }
    }

    // Recipe meta band (SERVES / PREP / COOK) — paragraphs wrapped in .recipe-meta
    .recipe-meta {
      display: inline-flex;
      flex-wrap: wrap;
      max-width: 100%;
      border: 1px solid var(--line);
      margin: 24px 0;
      font-family: var(--chrome);
    }
    .recipe-meta .article-full-body {
      margin: 0;
      padding: 11px 18px;
      font-size: 14px;
      color: var(--ink);
      border-right: 1px solid var(--line);
    }
    .recipe-meta .article-full-body:last-child {
      border-right: 0;
    }

    // Recipe nutrition panel (GOOD TO KNOW / PER SERVING) — wrapped in .recipe-nutrition
    .recipe-nutrition {
      font-family: var(--chrome);
      background: var(--panel);
      padding: 16px 20px 18px;
      margin: 26px 0 8px;
      font-size: 13px;
      line-height: 1.55;
      color: var(--ink-soft);
    }
    .recipe-nutrition .nutri-head {
      display: block;
      margin: 12px 0 4px;
      font-size: 11px;
      font-weight: bold;
      letter-spacing: 0.1em;
      text-transform: uppercase;
      color: var(--ink);

      &:first-child {
        margin-top: 0;
      }
    }
    .recipe-nutrition .nutri-item {
      display: inline;
      margin: 0;

      .ld_bold {
        font-weight: normal;
      }
    }

    // pull quotes -> blockquote.pull
    .article-pull-quote {
      border-left: 4px solid var(--red);
      margin: 34px 0;
      padding: 6px 0 6px 24px;
      font-size: 24px;
      line-height: 1.45;
      color: var(--ink);
      font-style: italic;
    }

    .article-pull-quote-credit {
      font-family: var(--chrome);
      font-style: normal;
      font-size: 12px;
      letter-spacing: 0.1em;
      text-transform: uppercase;
      color: var(--ink-soft);
      margin: -24px 0 24px;
    }

    // boxout -> "did you know" panel
    .article-boxout {
      font-family: var(--chrome);
      background: var(--panel);
      border-left: 4px solid var(--red);
      padding: 16px 20px;
      margin: 30px 0;
      font-size: 14px;
      line-height: 1.5;
    }

    // images -> figure
    .article-in-image {
      margin: 22px 0;
    }

    .article-in-image img,
    .article-hero__media img,
    img {
      max-width: 100%;
      height: auto;
      display: block;
      margin: 0 auto;
    }

    .articleImageCaption {
      font-family: var(--chrome);
      font-size: 12px;
      color: var(--ink-soft);
      padding: 8px 0 10px;
      border-bottom: 1px solid var(--line);
    }

    table {
      width: 100%;
      border-collapse: collapse;
      margin: 1rem 0;
      font-size: 0.85rem;
    }

    th,
    td {
      border: 1px solid var(--line);
      padding: 0.35rem 0.5rem;
      text-align: left;
    }

    th {
      background: var(--panel);
    }

    .dropcap:first-letter {
      font-weight: bold;
    }

    // large decorative drop cap for a body-copy opener (scoped to body paragraphs so it
    // never affects .dropcap used on recipe steps)
    .article-full-body.dropcap:first-letter {
      float: left;
      font-family: var(--serif);
      font-size: 58px;
      line-height: 0.8;
      font-weight: bold;
      color: var(--red);
      padding: 4px 10px 0 0;
    }

    // "By the numbers" stat callouts (wrapped in .article-stats; each .stat = big figure + caption)
    .article-stats {
      margin: 34px 0;
      border-top: 2px solid var(--red);
    }
    .stat {
      padding: 14px 0 12px;
      border-bottom: 1px solid var(--line);
    }
    .stat-value {
      font-family: var(--chrome);
      font-weight: bold;
      font-size: 32px;
      line-height: 1.05;
      color: var(--ink);
      margin: 0;
    }
    .stat-label {
      font-family: var(--chrome);
      font-size: 13px;
      line-height: 1.4;
      color: var(--ink-soft);
      margin: 5px 0 0;
    }

    .ld_bold {
      font-weight: bold;
    }

    .ld_italic {
      font-style: italic;
    }

    .ld_superscript {
      vertical-align: super;
      font-size: 0.7em;
    }

    // ---- Group-test review blocks (HSTF etc.) ----------------------------
    // Bike model name -> white-on-black bar (matches print)
    .bike-title {
      display: inline-block;
      background: var(--ink);
      color: #fff;
      font-family: var(--chrome);
      font-weight: bold;
      font-size: 20px;
      line-height: 1.15;
      padding: 8px 14px;
      margin: 42px 0 16px;
    }

    // Mini sub-heading within a review ("What's the story?", "Key specs") — accent red
    .review-q {
      font-family: var(--chrome);
      font-weight: bold;
      font-size: 15px;
      color: var(--red);
      margin: 22px 0 8px;
    }

    // Compact "Key specs" list
    .spec-list {
      font-family: var(--chrome);
      font-size: 14px;
      line-height: 1.4;
      color: var(--ink);
      margin: 6px 0 22px;
    }
    .spec-list .spec-item {
      margin: 0 0 4px;
    }

    // "STUFF SAYS" verdict bar: rule above, black label, accent-red verdict, black stars
    .verdict {
      font-family: var(--chrome);
      border-top: 2px solid var(--ink);
      padding: 12px 0 2px;
      margin: 20px 0 26px;
      font-size: 14px;
      line-height: 1.45;
      color: var(--ink);
    }
    .verdict .verdict-label {
      font-weight: bold;
      text-transform: uppercase;
      letter-spacing: 0.04em;
      color: var(--ink);
      margin-right: 6px;
    }
    .verdict .verdict-text {
      color: var(--red);
    }
    .verdict .verdict-stars {
      white-space: nowrap;
      margin-left: 6px;
      font-size: 16px;
      letter-spacing: 1px;
    }
    .verdict .star.on {
      color: var(--ink);
    }
    .verdict .star.off {
      color: var(--line);
    }

    // "BEST FOR ..." award badge -> stacked white-on-black box (matches print)
    .award-badge {
      display: inline-block;
      font-family: var(--chrome);
      text-transform: uppercase;
      color: #fff;
      background: var(--ink);
      padding: 8px 14px;
      margin: 8px 0 20px;
      line-height: 1.15;
    }
    .award-badge .award-for {
      display: block;
      font-size: 10px;
      font-weight: bold;
      letter-spacing: 0.08em;
      color: #b9b9b9;
    }
    .award-badge .award-cat {
      display: block;
      font-size: 15px;
      font-weight: bold;
      letter-spacing: 0.03em;
    }

    a {
      color: var(--red);
      text-decoration: underline;
    }
  }
}

a.btn {
  display: inline-block;
  background: var(--green);
  color: #fff;
  font-family: var(--chrome);
  font-weight: bold;
  font-size: 13px;
  line-height: normal;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  padding: 11px 20px;
  border: 0;
  border-radius: 0;
  cursor: pointer;
  text-align: center;
  position: relative;

  &:hover {
    background: var(--green-dark);
    color: #fff;
    text-decoration: none;
  }

  &.block {
    display: block;
  }

  &.is-loading {
    cursor: default;
    pointer-events: none;

    .btn-label {
      visibility: hidden;
    }
  }

  .btn-spinner {
    position: absolute;
    top: 50%;
    left: 50%;
    width: 16px;
    height: 16px;
    margin: -8px 0 0 -8px;
    border: 2px solid rgba(255, 255, 255, 0.4);
    border-top-color: #fff;
    border-radius: 50%;
    animation: btn-spin 0.6s linear infinite;
  }
}

@keyframes btn-spin {
  to {
    transform: rotate(360deg);
  }
}

.mini-cover img,
.cover img {
  width: 100%;
  height: auto;
  border: 1px solid var(--line);
  box-shadow: 2px 2px 0 rgba(0, 0, 0, 0.08);
}

/* End-of-article conversion panel */
.end-cta {
  border: 1px solid var(--line);
  margin: 44px 0 10px;

  .cta-head {
    background: var(--bar);
    color: #fff;
    font-size: 13px;
    font-weight: bold;
    text-transform: uppercase;
    letter-spacing: 0.08em;
    padding: 10px 20px;
  }

  .cta-body {
    display: flex;
    gap: 24px;
    padding: 22px 20px;
    align-items: center;
    flex-wrap: wrap;
  }

  .cover {
    width: 110px;
    flex: none;
  }

  .offer {
    flex: 1;
    min-width: 220px;

    h3 {
      margin: 0 0 6px;
      font-size: 19px;
      font-family: var(--chrome);
      font-weight: bold;
      color: var(--ink);
    }
  }

  .price {
    font-size: 15px;
    margin: 0 0 10px;

    b {
      color: var(--red);
      font-size: 22px;
    }

    s {
      color: var(--ink-soft);
    }

    .save-inline {
      font-size: 14px;
      color: var(--green);
    }
  }

  ul {
    list-style: none;
    margin: 0;
    padding: 0;
    font-size: 13px;
    color: var(--ink-soft);
  }

  li {
    padding: 3px 0 3px 20px;
    position: relative;

    &::before {
      content: '✓';
      position: absolute;
      left: 0;
      color: var(--green);
      font-weight: bold;
    }
  }

  .buy-col {
    display: flex;
    flex-direction: column;
    gap: 8px;
    min-width: 180px;

    .see-all {
      text-align: center;
      font-size: 12px;
    }

    small {
      font-size: 11px;
      color: var(--ink-soft);
      text-align: center;
    }
  }
}

/* Right rail */
.rail {
  font-size: 14px;
}

.rail-sticky {
  position: sticky;
  top: 20px;
  display: flex;
  flex-direction: column;
  gap: 26px;
}

.rail-box {
  border: 1px solid var(--line);

  .rail-head {
    background: var(--red);
    color: #fff;
    font-size: 13px;
    font-weight: bold;
    text-transform: uppercase;
    letter-spacing: 0.08em;
    padding: 9px 14px;

    &.grey {
      background: var(--bar);
    }
  }

  .rail-inner {
    padding: 16px 14px;
  }
}

.rail-offer {
  .cover {
    width: 130px;
    margin: 0 auto 14px;
  }

  h3 {
    margin: 14px 0 4px;
    font-size: 16px;
    text-align: center;
    font-family: var(--chrome);
    font-weight: bold;
    color: var(--ink);
  }

  .price {
    text-align: center;
    font-size: 13px;
    margin: 0 0 4px;

    b {
      color: var(--red);
      font-size: 19px;
    }
  }

  .save {
    text-align: center;
    font-size: 12px;
    color: var(--green);
    font-weight: bold;
    margin: 0 0 12px;
  }

  small {
    display: block;
    text-align: center;
    font-size: 11px;
    color: var(--ink-soft);
    margin-top: 8px;
  }
}

.newsletter {
  p {
    font-size: 13px;
    color: var(--ink-soft);
    margin: 0 0 10px;
  }

  .btn {
    width: 100%;
    box-sizing: border-box;
  }

  small {
    display: block;
    font-size: 10px;
    color: #8a8a8a;
    margin-top: 8px;
    line-height: 1.5;
  }
}

.more-reads {
  ul {
    list-style: none;
    margin: 0;
    padding: 0;
  }

  li {
    border-bottom: 1px solid var(--line);
    padding: 11px 0;

    &:last-child {
      border-bottom: 0;
      padding-bottom: 2px;
    }
  }

  a {
    color: var(--ink);
    font-size: 13px;
    line-height: 1.45;
    display: block;

    &:hover {
      color: var(--red);
    }
  }

  .cat {
    display: block;
    font-size: 10px;
    font-weight: bold;
    letter-spacing: 0.12em;
    text-transform: uppercase;
    color: var(--red);
    margin-bottom: 3px;
  }
}

/* Related articles */
.related {
  border-top: 3px solid var(--bar);
  padding: 26px 0 44px;

  h2 {
    font-size: 18px;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    margin: 0 0 18px;
    font-family: var(--chrome);
    font-weight: bold;
    color: var(--ink);

    span {
      color: var(--red);
    }
  }
}

.card-row {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 24px;
}

.card {
  border: 1px solid var(--line);
  display: flex;
  flex-direction: column;

  .card-media {
    display: block;
    aspect-ratio: 16 / 10;
    overflow: hidden;

    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
      display: block;
    }
  }

  .ph {
    width: 100%;
    height: 100%;
    box-sizing: border-box;
    font-size: 10px;
    padding: 12px;
    background: linear-gradient(135deg, #e8e2d8, #cdb9a5 55%, #a8551f);
    display: flex;
    align-items: flex-end;
    color: #fff;
    letter-spacing: 0.12em;
    text-transform: uppercase;
  }

  .card-txt {
    padding: 14px 16px 18px;
    display: flex;
    flex-direction: column;
    gap: 8px;
    flex: 1;
  }

  .cat {
    font-size: 10px;
    font-weight: bold;
    letter-spacing: 0.12em;
    text-transform: uppercase;
    color: var(--red);
  }

  h3 {
    font-family: var(--serif);
    font-weight: normal;
    font-size: 18px;
    line-height: 1.3;
    margin: 0;
    color: var(--ink);

    a {
      color: var(--ink);

      &:hover {
        color: var(--red);
      }
    }
  }

  .meta {
    font-size: 11px;
    color: var(--ink-soft);
    margin-top: auto;
  }
}

.article-footer {
  background: var(--bar);
  color: #d8d8d8;
  font-size: 12px;
  margin-top: 10px;

  .wrap {
    display: flex;
    justify-content: space-between;
    flex-wrap: wrap;
    gap: 10px;
    padding-top: 18px;
    padding-bottom: 18px;
  }

  a {
    color: #fff;
  }
}

@media (max-width: 960px) {
  .article-grid {
    // minmax(0, 1fr) (not 1fr) so a wide image can't force the track past the viewport
    grid-template-columns: minmax(0, 1fr);
  }

  .rail {
    max-width: 520px;
  }
}

@media (max-width: 820px) {
  .card-row {
    grid-template-columns: 1fr;
  }
}
</style>
